import { createContext, type ReactNode, useContext, useState } from 'react';
import { PhieuXuatTraLaiNhaCungCap, PhieuXuatTraLaiNhaCungCapInput } from '@/types/schemas';
import { usePhieuXuatTraLaiNhaCungCapExport, useSearchFieldState } from './hooks';
import { MA_CHUNG_TU, QUERY_KEYS } from '@/constants';
import { useCRUD, useForms, usePatch } from '@/hooks';

type ExportValue = ReturnType<typeof usePhieuXuatTraLaiNhaCungCapExport>;
type FormsValue = ReturnType<typeof useForms>;
type CRUDValue = ReturnType<typeof useCRUD<PhieuXuatTraLaiNhaCungCap, PhieuXuatTraLaiNhaCungCapInput>>;
type PatchValue = ReturnType<typeof usePatch>;

interface SearchDialogState {
  showSearchDialog: boolean;
  handleSearch: () => void;
  handleSearchClose: () => void;
  searchFieldState: ReturnType<typeof useSearchFieldState>;
}

interface ImportDialogState {
  isImportDialogOpen: boolean;
  openImportDialog: () => void;
  closeImportDialog: () => void;
}

interface PhieuXuatTraLaiNhaCungCapContextValue {
  export: ExportValue;
  forms: FormsValue;
  crud: CRUDValue;
  patch: PatchValue;
  search: SearchDialogState;
  import: ImportDialogState;
  ngayCt: string | undefined;
  setNgayCt: (value: string | undefined) => void;
  transactionOptions: { value: string; label: string }[];
}

const PhieuXuatTraLaiNhaCungCapContext = createContext<PhieuXuatTraLaiNhaCungCapContextValue | null>(null);

export const usePhieuXuatTraLaiNhaCungCapContext = () => {
  const context = useContext(PhieuXuatTraLaiNhaCungCapContext);
  if (!context) {
    throw new Error('usePhieuXuatTraLaiNhaCungCapContext must be used within PhieuXuatTraLaiNhaCungCapProvider');
  }
  return context;
};

export const PhieuXuatTraLaiNhaCungCapProvider = ({ children }: { children: ReactNode }) => {
  const exportValue = usePhieuXuatTraLaiNhaCungCapExport();
  const crudValue = useCRUD<PhieuXuatTraLaiNhaCungCap, PhieuXuatTraLaiNhaCungCapInput>({
    endpoint: QUERY_KEYS.PHIEU_XUAT_TRA_LAI_NHA_CUNG_CAP,
    sumField: 't_tt_nt'
  });
  const { data } = useCRUD({
    endpoint: `${QUERY_KEYS.MA_GIAO_DICH}?ma_ct=${MA_CHUNG_TU.MUA_HANG.PHIEU_XUAT_TRA_LAI_NHA_CUNG_CAP}`
  });
  const transactionOptions = data.map(item => ({
    value: item.uuid,
    label: item.ten_gd
  }));
  const formsValue = useForms(crudValue.fetchDetail);
  const patchValue = usePatch({ endpoint: `${QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC}/chi-tiet-hoa-don` });
  const [ngayCt, setNgayCt] = useState<string | undefined>(undefined);

  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const handleSearch = () => setShowSearchDialog(true);
  const handleSearchClose = () => setShowSearchDialog(false);

  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const openImportDialog = () => setIsImportDialogOpen(true);
  const closeImportDialog = () => setIsImportDialogOpen(false);

  const searchFieldState = useSearchFieldState();

  const searchValue: SearchDialogState = {
    showSearchDialog,
    handleSearch,
    handleSearchClose,
    searchFieldState
  };

  const importValue: ImportDialogState = {
    isImportDialogOpen,
    openImportDialog,
    closeImportDialog
  };

  const value: PhieuXuatTraLaiNhaCungCapContextValue = {
    export: exportValue,
    forms: formsValue,
    crud: crudValue,
    patch: patchValue,
    search: searchValue,
    import: importValue,
    ngayCt,
    setNgayCt,
    transactionOptions
  };

  return (
    <PhieuXuatTraLaiNhaCungCapContext.Provider value={value}>{children}</PhieuXuatTraLaiNhaCungCapContext.Provider>
  );
};
